import { sharePointHelpers } from '@qdabra/forms-shared/sharePoint';
import {
    byteArrayToBase64,
    decodeBase64Attachment,
} from '@qdabra/infopath-attachments';
import { zip } from 'ramda';
import {
    FILE_ATTACH_URL_KEY,
    FILE_DATA_KEY,
    IS_LOCAL_KEY,
} from '../../constants/file.constants';
import { getNodeData, setNodeData } from '../../utility/nodeDataHelpers';
import { combinePath } from '../dataConnection.utils';
import shpListAdapter from '../sharePointList/sharePointListAdapter';
import { LIST_FORM_IDXPATH } from './sharePointListForm.constants';
import {
    selectListFormNode,
    selectListFormNodes,
} from './sharePointListForm.getDsFieldValues';
import { getItemContainer } from './sharePointListForm.getItemsForSubmit';

const { submitListAttachmentAsync } = sharePointHelpers;

function sharePointListFormAdapter(
    definition,
    template,
    dataSources,
    shpAccess,
    settings,
) {
    let shpListAdapterInstance;
    let listId = definition.listId;
    let listName = '';
    let siteUrl = combinePath(template.getBasePath(), definition.siteURL);

    const fields = definition.fields;

    const setIdAsync = async (item, id) => {
        if (!id) {
            return;
        }

        const selectedNode = selectListFormNode(
            item.node.node,
            LIST_FORM_IDXPATH,
        );

        if (!selectedNode) {
            return;
        }

        await selectedNode.setValueAsync(id);
    };

    const updateSubmittedFileField = async (
        attachment,
        attachmentUrlNode,
        attachmentData,
    ) => {
        await attachment.setValueAsync(attachmentData.attachmentUrl);

        setNodeData(attachmentUrlNode, FILE_DATA_KEY, null);
        setNodeData(
            attachmentUrlNode,
            FILE_ATTACH_URL_KEY,
            attachmentData.fileName,
        );
        setNodeData(attachmentUrlNode, IS_LOCAL_KEY, false);
    };

    const shouldUseListName = () => !listId;

    function getAttachmentBase64(attachmentUrlNode) {
        const attachmentData = getNodeData(attachmentUrlNode, FILE_DATA_KEY);

        if (!attachmentData) {
            return null;
        }

        const decoded = decodeBase64Attachment(attachmentData);
        const base64Value = byteArrayToBase64(decoded.bytes);

        return base64Value;
    }

    async function processAttachmentFilesAsync(item, id) {
        const attachments = selectListFormNodes(
            item.node.node,
            'my:Attachments/attachmentURL',
        );

        for (const attachment of attachments) {
            //Case 50643 - Attachments saved from list forms are unusable
            const attachmentUrlNode = attachment.getNode();
            const base64Value = getAttachmentBase64(attachmentUrlNode);

            if (base64Value) {
                const fileName = getNodeData(
                    attachmentUrlNode,
                    FILE_ATTACH_URL_KEY,
                );
                const data = await submitListAttachmentAsync(
                    shpAccess,
                    id,
                    fileName,
                    base64Value,
                    siteUrl,
                    listId || listName,
                    shouldUseListName(),
                );
                await updateSubmittedFileField(
                    attachment,
                    attachmentUrlNode,
                    data,
                );
            }
        }
    }

    const processItemsAttachments = async (itemIdPairs) => {
        for (const [item, id] of itemIdPairs) {
            await processAttachmentFilesAsync(item, id);
        }
    };

    const assignItemsIds = async (itemIdPairs) => {
        for (const [item, id] of itemIdPairs) {
            await setIdAsync(item, id);
        }
    };

    const processSubmittedResultAsync = async (items, data) => {
        // array of 2-dimensional arrays
        const itemIdPairs = zip(items, data).map(([item, { id }]) => [
            item,
            id,
        ]);

        await assignItemsIds(itemIdPairs);

        await processItemsAttachments(itemIdPairs);
    };

    const performSubmitAsync = async () => {
        const container = getItemContainer(
            siteUrl,
            listId,
            listName,
            dataSources,
            fields,
        );

        if (!container) {
            console.error('Could not find list item(s) to submit');
            return;
        }

        const data = await shpAccess.submitToListAsync(container);

        await processSubmittedResultAsync(container.itemArray, data);
    };

    function initAsync(dataSource) {
        if (!shpListAdapterInstance) {
            shpListAdapterInstance = shpListAdapter(
                definition,
                template,
                shpAccess,
                settings,
            );
        }

        return shpListAdapterInstance.initAsync(dataSource);
    }

    function executeAsync() {
        return performSubmitAsync();
    }

    function executeQueryAsync(dataSource) {
        return shpListAdapterInstance.executeQueryAsync(dataSource);
    }

    function queryByIdAsync(dataSource, listItemId) {
        return shpListAdapterInstance.queryByIdAsync(dataSource, listItemId);
    }

    function getUrl() {
        return siteUrl;
    }

    function setUrlAndListInfo(spSiteUrl, spListId, spListName) {
        siteUrl = spSiteUrl;
        listId = spListId;
        listName = spListName;

        shpListAdapterInstance.setUrlAndListInfo(
            spSiteUrl,
            spListId,
            spListName,
        );
    }

    return {
        initAsync,
        executeAsync,
        executeQueryAsync,
        getUrl,
        queryByIdAsync,
        setUrlAndListInfo,
    };
}

export default sharePointListFormAdapter;
